import { useEffect, useState } from "react";
import styled from "styled-components";

import { ActionButton } from "./ActionButton";
import { MicIcon, MicMuteIcon, MicSpeakingIcon } from "./icons/MicIcon";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { useAutoMuteLocalMic } from "hooks/useAutoMuteLocalMic";
import { colors } from "utils";

export const ToggleLocalMicButton = ({ style }) => {
  const { localTracks, room } = useVideoContext();
  const hasAudioTrack = localTracks.some((track) => track.kind === "audio");
  const [isSpeaking, setIsSpeaking] = useState(false);

  const { isAudioMuted, isAutoMuted, isSoloTime, toggleMic } = useAutoMuteLocalMic();

  // Detect speaking state from LiveKit local participant
  useEffect(() => {
    if (!room?.localParticipant) return;

    const handleSpeakingChanged = (speaking) => {
      setIsSpeaking(speaking);
    };

    room.localParticipant.on("isSpeakingChanged", handleSpeakingChanged);
    return () => {
      room.localParticipant.off("isSpeakingChanged", handleSpeakingChanged);
    };
  }, [room?.localParticipant]);

  const getMicState = () => {
    if (!hasAudioTrack || isAudioMuted || isAutoMuted) return "muted";
    if (isSpeaking) return "speaking";
    return "default";
  };

  const micState = getMicState();

  const getIcon = () => {
    switch (micState) {
      case "muted":
        return <MicMuteIcon />;
      case "speaking":
        return <MicSpeakingIcon />;
      default:
        return <MicIcon />;
    }
  };

  const getTooltip = () => {
    if (isSoloTime) return "Microphone disabled during Solo Time";
    if (isAutoMuted) return "";
    if (!hasAudioTrack) return "No Audio Device Found";
    return isAudioMuted ? "Unmute Microphone" : "Mute Microphone";
  };

  return (
    <StyledActionButton
      onClick={isAutoMuted ? undefined : toggleMic}
      style={style}
      tooltip={getTooltip()}
      ariaLabel="Microphone"
      $micState={micState}
      $disabled={isAutoMuted}
    >
      {getIcon()}
    </StyledActionButton>
  );
};

const StyledActionButton = styled(ActionButton)`
  ${(props) =>
    props.$micState === "muted" &&
    `color: ${colors.pink500};`}
  ${(props) =>
    props.$disabled &&
    `opacity: 0.25; pointer-events: none; cursor: default;`}
`;
